import type { FeatureModule, FeatureState } from '../types';
import { beginStylePass, endStylePass } from './style-pass-guard';

export default function createTextSizeModule(): FeatureModule {
  let currentSize = 100;
  let observer: MutationObserver | null = null;
  let refreshQueued = false;
  const STYLE_ID = 'accessify-text-size-styles';
  const STORAGE_KEY = 'accessify-text-size';
  const PROCESSED_ATTR = 'data-accessify-text-size-processed';
  const ORIGINAL_INLINE_ATTR = 'data-accessify-original-inline-font-size';
  const ORIGINAL_COMPUTED_ATTR = 'data-accessify-original-computed-font-size';
  const MIN_SIZE = 75;
  const MAX_SIZE = 200;
  const STEP = 5;
  const SKIP_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'NOSCRIPT',
    'IFRAME',
    'SVG',
    'PATH',
    'G',
    'DEFS',
    'MASK',
    'BR',
    'META',
    'LINK',
    'HEAD',
  ]);

  function ensureStyleMarker(percent: number) {
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `/* accessify text-size: ${percent}% */`;
  }

  function removeStyleMarker() {
    document.getElementById(STYLE_ID)?.remove();
  }

  function hasOwnTextContent(el: HTMLElement): boolean {
    return Array.from(el.childNodes).some((node) => {
      return node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim());
    });
  }

  function shouldScaleElement(el: HTMLElement): boolean {
    if (el.id === 'accessify-root' || el.closest('#accessify-root')) return false;
    if (SKIP_TAGS.has(el.tagName)) return false;

    const computed = window.getComputedStyle(el);
    const fontSize = parseFloat(computed.fontSize);
    if (!Number.isFinite(fontSize) || fontSize <= 0) return false;
    if (computed.display === 'none' || computed.visibility === 'hidden') return false;

    // body must always scale even when it carries no direct text node.
    // Reason: any text that lives directly under body (or in a descendant
    // without its own font-size) inherits the body's font-size. Without
    // this branch, R6-2 reproduces — body stays at 16 px while we scale
    // h1/p/a, so inherited bits look untouched.
    if (el === document.body) return true;

    const interactiveTags = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'LABEL', 'OPTION']);
    return interactiveTags.has(el.tagName) || hasOwnTextContent(el);
  }

  function getTargetElements(): HTMLElement[] {
    if (!document.body) return [];
    const elements = [document.body, ...Array.from(document.body.querySelectorAll<HTMLElement>('*'))];
    return elements.filter(shouldScaleElement);
  }

  function rememberOriginalSize(el: HTMLElement) {
    if (!el.hasAttribute(ORIGINAL_INLINE_ATTR)) {
      el.setAttribute(ORIGINAL_INLINE_ATTR, el.style.fontSize || '');
    }

    if (!el.hasAttribute(ORIGINAL_COMPUTED_ATTR)) {
      const computedSize = parseFloat(window.getComputedStyle(el).fontSize);
      if (Number.isFinite(computedSize) && computedSize > 0) {
        el.setAttribute(ORIGINAL_COMPUTED_ATTR, String(computedSize));
      }
    }
  }

  function scaleElement(el: HTMLElement, scale: number) {
    rememberOriginalSize(el);

    const originalSize = parseFloat(el.getAttribute(ORIGINAL_COMPUTED_ATTR) || '');
    if (!Number.isFinite(originalSize) || originalSize <= 0) return;

    const nextSize = Math.round(originalSize * scale * 100) / 100;
    el.style.setProperty('font-size', `${nextSize}px`, 'important');
    el.setAttribute(PROCESSED_ATTR, 'true');
  }

  function restoreElement(el: HTMLElement) {
    const originalInline = el.getAttribute(ORIGINAL_INLINE_ATTR);
    if (originalInline === null) return;

    if (originalInline) {
      el.style.fontSize = originalInline;
    } else {
      el.style.removeProperty('font-size');
    }

    el.removeAttribute(PROCESSED_ATTR);
    el.removeAttribute(ORIGINAL_INLINE_ATTR);
    el.removeAttribute(ORIGINAL_COMPUTED_ATTR);
  }

  function restoreProcessedElements() {
    document.querySelectorAll<HTMLElement>(`[${PROCESSED_ATTR}]`).forEach((el) => {
      restoreElement(el);
    });
  }

  function queueRefresh() {
    if (refreshQueued || currentSize === 100) return;

    refreshQueued = true;
    const flush = () => {
      refreshQueued = false;
      applySize(currentSize);
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => flush());
    } else {
      window.setTimeout(flush, 0);
    }
  }

  function startObserver() {
    if (observer || !document.body) return;

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
          queueRefresh();
          return;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function stopObserver() {
    observer?.disconnect();
    observer = null;
    refreshQueued = false;
  }

  function applySize(percent: number) {
    // Open a "style pass" so text-simplify's MutationObserver ignores the
    // cascade of mutations our font-size writes provoke (Framer / React
    // re-render subtrees on style change). Close one rAF later so the
    // framework's reactive update falls inside the pass too.
    beginStylePass();
    try {
      if (percent === 100) {
        stopObserver();
        removeStyleMarker();
        restoreProcessedElements();
        return;
      }

      ensureStyleMarker(percent);

      const scale = percent / 100;
      const targets = new Set(getTargetElements());

      document.querySelectorAll<HTMLElement>(`[${PROCESSED_ATTR}]`).forEach((el) => {
        if (!targets.has(el)) {
          restoreElement(el);
        }
      });

      targets.forEach((el) => {
        scaleElement(el, scale);
      });

      startObserver();
    } finally {
      // Defer the close so frameworks have a frame to apply their reaction
      // to our style writes. The pass stays open across that frame.
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(endStylePass);
      } else {
        window.setTimeout(endStylePass, 0);
      }
    }
  }

  function clamp(value: number): number {
    return Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(value / STEP) * STEP));
  }

  function activate() {
    const saved = localStorage.getItem(STORAGE_KEY);
    currentSize = saved ? clamp(parseInt(saved, 10)) : 150;
    applySize(currentSize);
    localStorage.setItem(STORAGE_KEY, String(currentSize));
  }

  function deactivate() {
    currentSize = 100;
    applySize(100);
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'text-size',
    name: () => 'Text Size',
    description: 'Adjust text size from 100% to 200%',
    icon: 'text-size',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'text-size',
      enabled: currentSize !== 100,
      value: currentSize,
    }),
    setState: (size: number) => {
      currentSize = clamp(size);
      applySize(currentSize);
      localStorage.setItem(STORAGE_KEY, String(currentSize));
    },
  };
}
